import { Button, Flex, Stack, Text } from '@chakra-ui/react'
import { faEnvelopeOpenText } from '@fortawesome/pro-duotone-svg-icons'
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons'
import { usePathname, useSearchParams } from 'next/navigation'
import { useState } from 'react'

import logger from '@/utils/logger'

import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import { getGmailVerificationSearchUrl } from '@/client/services/url'

import AuthLink from '@/components/Auth/AuthLink'
import ShwLink from '@/components/ShwLink'
import TextWithLines from '@/components/TextWithLines/TextWithLines'
import { useToaster } from '@/components/Toast/useToaster'
import Alert from '@/components/UI/Alert'
import Icon from '@/components/UI/Icon/Icon'

import RestrictedPageAccess from './RestrictedPageAccess'

const GMAIL_DOMAIN = '@gmail.com'

function GmailLogo(props: React.ComponentProps<'svg'>): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="52 42 88 66" {...props}>
      <path fill="#4285f4" d="M58 108h14V74L52 59v43c0 3.32 2.69 6 6 6" />
      <path fill="#34a853" d="M120 108h14c3.32 0 6-2.69 6-6V59l-20 15" />
      <path fill="#fbbc04" d="M120 48v26l20-15v-8c0-7.42-8.47-11.65-14.4-7.2" />
      <path fill="#ea4335" d="M72 74V48l24 18 24-18v26L96 92" />
      <path fill="#c5221f" d="M52 51v8l20 15V48l-5.6-4.2C60.46 39.35 52 43.58 52 51" />
    </svg>
  )
}

export default function RestrictedPageVerifyEmail(): JSX.Element {
  const user = useUser()
  const searchParams = useSearchParams()
  const toast = useToaster()
  const pathname = usePathname()
  const [resentVerification, setResentVerification] = useState(false)
  const isVerifyingEmail = searchParams?.get('verifying_email') != null
  const returnTo = `${pathname}?${searchParams?.toString()}`

  const [onResendVerification, isResending] = useAPIWrapper(
    async () => {
      setResentVerification(true)
      toast({
        title: 'Email verification has been sent!',
        status: 'success',
      })
      try {
        await RPC.UNSAFE_sendVerificationEmail({})
      } catch (err) {
        logger.error(err)
        toast({
          title: (
            <TextWithLines text={`${err?.message ?? err}`} maxLines={Number.POSITIVE_INFINITY} />
          ),
          status: 'error',
        })
        setResentVerification(false)
      }
    },
    [toast],
    'Resend Verification'
  )

  return (
    <RestrictedPageAccess
      alignCenter
      faIcon={faEnvelopeOpenText}
      iconColor="primary.solid"
      title="Please verify your email"
      hideHomeButton
      hideLoginButton
      description={
        <Stack direction="column" align="center" spacing="32px">
          {isVerifyingEmail ? (
            <Alert variant="warning">Your email has not been verified yet. Please try again.</Alert>
          ) : null}
          <Text>
            Check your inbox! We sent an email verification link to <b>{user.email}</b>.
          </Text>
          <Stack direction="column" align="center" spacing="12px" width="100%">
            <AuthLink mode="verify" width={['100%', '210px']} returnTo={returnTo}>
              <Button width="100%">I verified my email</Button>
            </AuthLink>
            {user.email.endsWith(GMAIL_DOMAIN) ? (
              <ShwLink href={getGmailVerificationSearchUrl(user.email)} width={['100%', '210px']}>
                <Button
                  variant="outline"
                  width={['100%', '210px']}
                  rightIcon={<Icon faIcon={faExternalLink} />}
                  leftIcon={<GmailLogo width="20px" style={{ padding: '2px' }} />}
                >
                  Open Gmail
                </Button>
              </ShwLink>
            ) : null}
            {resentVerification ? (
              <Text>Email verification has been re-sent (make sure to check spam as well).</Text>
            ) : (
              <Flex width="100%" justify="center">
                <Button
                  variant="outline"
                  isLoading={isResending}
                  onClick={onResendVerification}
                  width={['100%', '210px']}
                >
                  Resend verification email
                </Button>
              </Flex>
            )}
          </Stack>
        </Stack>
      }
    />
  )
}
